/**
 * Admin → Hidden companies (migration 232, routes/hiddenCompanies.js):
 * pseudo-employers like "Self-employed", "Freelance" or "Retired" that never
 * show as a company anywhere — Search's "specific companies", the companies
 * search, suggestions and imports. Their people stay. Add by name (exact,
 * any case) or by LinkedIn company link; Search results also have a Hide
 * link for admins.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../AuthContext';
import { SuggestChipInput, companyChipLabel } from '../V2Lab';

type Entry = { id: number; kind: 'name' | 'slug'; value: string; label: string | null; created_at: string };

export const HiddenCompaniesPanel: React.FC = () => {
  const { token } = useAuth();
  const headers = useCallback((): Record<string, string> => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [text, setText] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    fetch('/api/admin/hidden-companies', { headers: headers() })
      .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
      .then(({ ok, d }) => { if (ok) setEntries(d.entries || []); else setMsg(d.error || 'Could not load'); })
      .catch(() => setMsg('Could not load'));
  }, [headers]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const send = async (list: string[]) => {
    if (!list.length) return;
    const r = await fetch('/api/admin/hidden-companies', { method: 'POST', headers: headers(), body: JSON.stringify({ entries: list }) });
    const d = await r.json().catch(() => ({}));
    if (r.ok) { setMsg(d.added ? `Hidden: ${list.map(companyChipLabel).filter((v, i, a) => a.indexOf(v) === i).join(', ')}.` : 'Already hidden.'); load(); }
    else setMsg(d.error || 'Could not hide that');
    return r.ok;
  };
  // Picked from the suggestions: that company's page AND its exact name (so
  // every page called e.g. "Self Employed" goes); typed + Enter: the name.
  const pick = (values: string[]) => {
    const v = values[values.length - 1];
    if (!v) return;
    const name = companyChipLabel(v);
    send(v === name ? [v] : [v, name]);
  };
  const add = async () => {
    const list = text.split(/\n|;/).map((x) => x.trim()).filter(Boolean);
    if (await send(list)) setText('');
  };
  const remove = async (e: Entry) => {
    const r = await fetch(`/api/admin/hidden-companies/${e.id}`, { method: 'DELETE', headers: headers() });
    if (r.ok) setEntries((xs) => xs.filter((x) => x.id !== e.id));
  };

  const shown = entries.filter((e) => !filter.trim() || `${e.value} ${e.label || ''}`.toLowerCase().includes(filter.trim().toLowerCase()));

  return (
    <div className="card p-5 mb-6">
      <button className="w-full flex items-center justify-between text-left" onClick={() => setOpen(!open)}>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Hidden companies</h2>
          <p className="text-sm text-gray-500">Pretend employers ("Self-employed", "Freelance", "Retired"…) that never show as a company in Search, suggestions or imports. Their people stay.</p>
        </div>
        <span className="text-gray-400 text-sm">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="mt-4 space-y-4">
          <div>
            <div className="text-sm font-medium text-gray-700 mb-1">Hide a company</div>
            <SuggestChipInput kind="company" values={[]} onChange={pick} authHeaders={() => ({ Authorization: `Bearer ${token}` })}
              placeholder="Start typing a company name, then pick it — or type a name and press Enter" />
            <p className="text-[11px] text-gray-400 mt-1">Picking one hides that company and every other with exactly the same name.</p>
            {msg && <p className="text-xs text-gray-600 mt-1">{msg}</p>}
          </div>
          <details>
            <summary className="text-xs text-gray-500 cursor-pointer select-none">Paste a list instead</summary>
            <textarea
              id="hidden-co-add"
              className="input min-h-[80px] font-mono text-xs"
              placeholder={'One per line — a name (matched exactly, any case) or a LinkedIn company link\nSelf-employed\nhttps://www.linkedin.com/company/freelance-work'}
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
            <div className="flex items-center gap-3 mt-2">
              <button className="btn-primary text-sm" onClick={add} disabled={!text.trim()}>Hide these</button>
            </div>
          </details>
          <div>
            <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
              <span className="text-sm font-medium text-gray-700">{entries.length} hidden</span>
              <input className="input !w-56 text-sm" placeholder="Filter…" value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Filter hidden companies" />
            </div>
            <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-80 overflow-y-auto">
              {shown.map((e) => (
                <div key={e.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                  <div className="min-w-0">
                    <span className="text-gray-900">{e.label || e.value}</span>
                    <span className="ml-2 text-[11px] text-gray-400">{e.kind === 'slug' ? `LinkedIn page: ${e.value}` : 'by name'}</span>
                  </div>
                  <button className="text-xs text-gray-500 hover:text-red-600 shrink-0" onClick={() => remove(e)} title="Show this company again">Unhide</button>
                </div>
              ))}
              {!shown.length && <div className="px-3 py-4 text-sm text-gray-400 text-center">Nothing here.</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
